import { RewardNotifications, tSharedAppState } from 'common/modules/ReactShared';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IReward } from '../../../models/myProgress';

export interface IRewardNotificationsState {
  /** Earned-but-unannounced rewards, oldest first — shown one at a time. */
  queue: IReward[];
  loading: boolean;
  /** Set once the request settles so a route change does not refetch. */
  fetched: boolean;
  failed: boolean;
}

const initialState: IRewardNotificationsState = {
  queue: [],
  loading: false,
  fetched: false,
  failed: false
};

const getUnreadRewards = createAsyncThunk<
  RewardNotifications.RewardNotificationsApiTypes.IUnreadRewardsResponse,
  void,
  { state: tSharedAppState }
>('rewardNotifications/getUnreadRewards_request', async () => {
  return RewardNotifications.rewardNotificationsApi.getUnreadRewards();
});

const markRewardSeen = createAsyncThunk<number, number, { state: tSharedAppState }>(
  'rewardNotifications/markRewardSeen_request',
  async (rewardId) => {
    await RewardNotifications.rewardNotificationsApi.markRewardSeen(rewardId);

    return rewardId;
  }
);

const markAllRewardsSeen = createAsyncThunk<void, void, { state: tSharedAppState }>(
  'rewardNotifications/markAllRewardsSeen_request',
  async () => {
    await RewardNotifications.rewardNotificationsApi.markAllRewardsSeen();
  }
);

const rewardNotificationsSlice = createSlice({
  name: 'rewardNotifications',
  initialState,
  reducers: {
    /**
     * A badge may have just been granted server-side (a lesson or a word game
     * finished). Clearing `fetched` re-arms the announcement hook so it asks
     * again on its next render — the SPA's stand-in for a full page load.
     */
    invalidate: (state) => {
      state.fetched = false;
      state.failed = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getUnreadRewards.pending, (state) => {
      state.loading = true;
      state.failed = false;
    });
    builder.addCase(getUnreadRewards.fulfilled, (state, action) => {
      state.queue = action.payload.rewards;
      state.loading = false;
      state.fetched = true;
    });
    builder.addCase(getUnreadRewards.rejected, (state) => {
      state.loading = false;
      state.fetched = true;
      state.failed = true;
    });

    // The reward leaves the queue only once the server has it as seen, so a
    // failed acknowledgement shows the badge again on the next page load
    // rather than swallowing it.
    builder.addCase(markRewardSeen.fulfilled, (state, action) => {
      state.queue = state.queue.filter((reward) => reward.id !== action.payload);
    });

    builder.addCase(markAllRewardsSeen.fulfilled, (state) => {
      state.queue = [];
    });
  }
});

const { reducer } = rewardNotificationsSlice;

const actions = {
  getUnreadRewards,
  markRewardSeen,
  markAllRewardsSeen,
  invalidate: rewardNotificationsSlice.actions.invalidate
};

export { reducer, actions };
